// PortfolioPage.js
import React, { useState, useEffect, useRef } from 'react';
import './PortfolioPage.css';
// Import the SVG and imagesnpm
import sequinsTitle from './sequins.svg';
import iceDesertHero from './ice-desert-hero.png';
import hockeyStickConcrete from './ice-desert2-sm.mp4';
import jazzBearMascot from './JAZZ-BEAR.mp4';
import redMascotInvestigation from './red-mascot-investigation.png';
import fabricWorkshop from './fabric-workshop.png';
import skatingCharacter from './skating-character.png';
import icePerformanceShow from './ice-performance-show.png';
import logos from './logos.svg';


const PortfolioPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  
  // Refs for parallax sections
  const sectionsRef = useRef([]);
  
  // Password (you should change this to something secure)
  const CORRECT_PASSWORD = process.env.REACT_APP_PORTFOLIO_PASSWORD;
  
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
    } else {
      setError('Incorrect password');
      setPassword('');
    }
  };
  
  // Enhanced scroll animations with Intersection Observer
  useEffect(() => {
    if (!isAuthenticated) return;
    
    // Intersection Observer for section animations
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -20% 0px', // Trigger when 10% visible
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
    };
    
    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const section = entry.target;
        const intersectionRatio = entry.intersectionRatio;
        
        if (entry.isIntersecting) {
          // Add active class for section-level animations
          section.classList.add('section-active');
          
          // Animate individual elements within the section
          const animatedElements = section.querySelectorAll('[data-animate]');
          animatedElements.forEach((element, index) => {
            setTimeout(() => {
              element.classList.add('animate-in');
              // Force full opacity for fade elements when animated in
              if (element.hasAttribute('data-fade')) {
                element.style.opacity = '1';
              }
            }, index * 100);
          });
        }
        
        // Continuous parallax and fade effects based on scroll position
        if (intersectionRatio > 0) {
          // Apply parallax effects
          const parallaxElements = section.querySelectorAll('[data-parallax]');
          parallaxElements.forEach(element => {
            const speed = parseFloat(element.dataset.parallax) || 0.5;
            const rect = section.getBoundingClientRect();
            const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
            const yPos = -(scrollProgress * 100 * speed);
            element.style.transform = `translate3d(0, ${yPos}px, 0)`;
          });
          
          // Apply fade effects based on intersection ratio
          const fadeElements = section.querySelectorAll('[data-fade]');
fadeElements.forEach(element => {
  // Check if element has been animated in by Intersection Observer
  if (element.classList.contains('animate-in')) {
    // If it's been animated in, ensure it stays at full opacity
    element.style.opacity = '1';
  } else {
    // Otherwise, use the original fade logic but with better thresholds
    const fadeThreshold = parseFloat(element.dataset.fade) || 0.3;
    const opacity = Math.min(1, Math.max(0, intersectionRatio / fadeThreshold));
    element.style.opacity = opacity;
  }
});
        }
      });
    }, observerOptions);
    
    // Observe all sections
    sectionsRef.current.forEach(section => {
      if (section) {
        sectionObserver.observe(section);
      }
    });
    
    // Scroll handler for smooth continuous effects
    const handleScroll = () => {
      sectionsRef.current.forEach((section) => {
        if (section) {
          const rect = section.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          
          // Only apply effects to sections in or near viewport
          if (rect.bottom > 0 && rect.top < windowHeight) {
            const scrollProgress = (windowHeight - rect.top) / (windowHeight + rect.height);
            
            // Apply parallax effects
            const parallaxElements = section.querySelectorAll('[data-parallax]');
            parallaxElements.forEach(element => {
              const speed = parseFloat(element.dataset.parallax) || 0.5;
              const rect = section.getBoundingClientRect();
              const scrollProgress = (window.innerHeight - rect.top) / (window.innerHeight + rect.height);
              const yPos = scrollProgress * 100 * speed; // Positive for foreground elements
              element.style.transform = `translate3d(0, ${yPos}px, 0)`;
            });
            
            // Smooth parallax for background elements (slower/opposite)
            const bgParallax = section.querySelectorAll('[data-bg-parallax]');
            bgParallax.forEach(element => {
              const speed = parseFloat(element.dataset.bgParallax) || 0.2;
              const yPos = -(scrollProgress * 50 * speed); // Negative for backgrounds
              element.style.transform = `translate3d(0, ${yPos}px, 0)`;
            });
          }
        }
      });
    };
    
    // Throttled scroll listener
    let ticking = false;
    const throttledScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };
    
    window.addEventListener('scroll', throttledScroll);
    
    // Initial call
    handleScroll();
    
    // Cleanup
    return () => {
      window.removeEventListener('scroll', throttledScroll);
      sectionObserver.disconnect();
    };
  }, [isAuthenticated]);
  
  // Password protection screen
  if (!isAuthenticated) {
    return (
      <div className="password-screen">
        <div className="password-container">
          <div className="brand-header">
            <span className="brand-name1">sterling rain</span>
            <span className="brand-subtitle1">projects ltd.</span>
          </div>
          <form onSubmit={handlePasswordSubmit} className="password-form">
            <h2>Protect Your Deck</h2>
            <p>(Best viewed on a desktop or in portrait orientation)</p>
            <div className="input-group">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="password-input"
                autoFocus
              />
              <button type="submit" className="password-submit">
                Enter
              </button>
            </div>
            {error && <p className="error-message">{error}</p>}
          </form>
        </div>
      </div>
    );
  }
  
  // Main portfolio content based on PDF
  return (
    <div className="portfolio-page">

      {/* Section 0: TITLE PAGE*/}
      <section 
        className="portfolio-section premium-partnership"
        ref={el => sectionsRef.current[0] = el}
      >
        <div className="section-content">
          <div className="partnership-grid">
            <div className="brand-projects-horizontal" data-animate data-fade="0.3">
              <span className="brand-subtitle-horizontal" >projects ltd.</span>
            </div>
            <div className="brand-header-vertical" data-animate data-fade="0.5">
              <span className="brand-name-vertical">sterling rain</span>
            </div>
            <div className="partnership-content" data-animate data-fade="0.7">
              <h1 style={{fontWeight: '100', paddingLeft: '30px'}}>Mammoth and Jazz Preliminary Concepts</h1>
            </div>
          </div>
          
        </div>
      </section>

      {/* Section 1: Premium Content Partnership Overview */}
      <section 
        className="portfolio-section premium-partnership"
        ref={el => sectionsRef.current[1] = el}
      >
        <div className="section-content">
          <div className="partnership-grid">
            <div className="brand-projects-horizontal" data-animate data-fade="0.2">
              <span className="brand-subtitle-horizontal">projects ltd.</span>
            </div>
            <div className="brand-header-vertical" data-animate data-fade="0.3">
              <span className="brand-name-vertical">sterling rain</span>
            </div>
            <div className="partnership-content" data-animate data-fade="0.5">
              <h1 className="partnership-title">premium content partnership</h1>
              
              <div className="features-grid">
                <div className="feature-item" data-animate data-fade="0.6">
                  <h3>Multi-Platform Distribution</h3>
                  <p>Designed for YouTube streaming with strategic previews, trailers, and shorts optimized for social channels to maximize reach and engagement
across the entire digital ecosystem.
</p>
                </div>
                
                <div className="feature-item" data-animate data-fade="0.7">
                  <h3>Complete Ownership & Rights</h3>
                  <p>Work-for-hire structure ensures SEG Media retains full ownership in perpetuity across all digital and streaming platforms, providing complete control and flexibility for future distribution.</p>
                </div>
                
                <div className="feature-item" data-animate data-fade="0.8">
                  <h3>Seamless Brand Integration</h3>
                  <p>Brand and sponsor integrations woven naturally into the narrative wherever and whenever needed—because authentic partnerships enhance great storytelling.</p>
                </div>
                
                <div className="feature-item" data-animate data-fade="0.9">
                  <h3>Investment Level</h3>
                  <p>Production value and storytelling expertise starting at $35,000.</p>
                </div>
                
                <div className="feature-item" data-animate data-fade="1.0">
                  <h3>Flexible Episodic Format</h3>
                  <p>Episodic by design—each segment crafted between 3-4 minutes for social consumption or extended to 22 minutes for deeper storytelling, pending creative exploration and strategic goals.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Ice in the Desert - Title */}
      <section 
        className="portfolio-section ice-desert-title"
        ref={el => sectionsRef.current[2] = el}
      >
        <div className="section-background ice-desert-bg" style={{backgroundImage: `url(${iceDesertHero})`}}>
          <div className="glitch-overlay"></div>
        </div>
        <div className="ice-title-content">
          <h1 className="ice-title-text" data-animate data-fade="0.3">
              Ice in the Desert
            </h1>
          <p className="ice-subtitle" data-animate data-fade="0.7">The beautiful chaos of hockey culture thriving where it absolutely shouldn't</p>
        </div>
      </section>

      {/* Section 3: Ice in the Desert - Description */}
      <section 
        className="portfolio-section ice-desert-description"
        ref={el => sectionsRef.current[3] = el}
      >
        <div className="section-background ice-hockey-stick-bg">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="background-video"
          >
            <source src={hockeyStickConcrete} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="section-content">
          <div className="ice-description-layout">
            <div className="ice-description-text" data-animate data-fade="0.7">
              <p className="ice-main-description">
                The immersive storytelling of <span className="emphasized-text">The Last Dance</span> meets the love of process in <span className="emphasized-text">Chef's Table</span> but for the most unexpectedly passionate hockey community in America. We're talking about families flooding their backyards in 20-degree weather at 4,000 feet, kids practicing slapshots on concrete at midnight, and the grassroots foundation that makes NHL dreams possible.
              </p>
              <p className="ice-tagline" data-animate data-fade="0.9">
                Golden hour desert landscapes with steam rising off fresh ice. Handheld, intimate camera work that feels like you're part of the family building this dream. Time-lapse content of ice formation and community gathering that showcases magical transformation happening in the desert.
                </p><p className="ice-tagline" data-animate data-fade="0.6"><span className="emphasized-text">This is process-heavy content that delivers pure dopamine hits of community coming together over something beautifully incongruous.
              </span></p>
            </div>
          </div>
        </div>
      </section>

      
     {/* Section 4: Jazz Bear Selects - Title */}
      <section 
        className="portfolio-section jazz-bear-title"
        ref={el => sectionsRef.current[4] = el}
      >
        <div className="section-background jazz-bear-bg">
          <video 
            autoPlay 
            loop 
            muted 
            playsInline
            className="background-video"
          >
            <source src={jazzBearMascot} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="section-content">
          {/* Top content */}
          <div className="jazz-title-content" data-animate data-fade="0.5">
            <h1 className="jazz-main-title">
              <span className="jazz-title-text">JAZZ BEAR SELECTS</span>
            </h1>
            <p className="jazz-subtitle">Mock investigative chaos meets earnest mascot obsession</p>
          </div>
          
          {/* Bottom content */}
          <div className="jazz-description-text-container" data-animate data-fade="0.7">
            <div className="jazz-description-text">
              We're going full investigative journalism mode on the most delightfully ridiculous question in sports: What does a mammoth mascot actually look like?
            </div>
            <div className="jazz-description-text">
              Picture <em>Nathan for You</em> with the access and authenticity of <em>Subway Takes</em> going deep on mascot development.
            </div>
          </div>
        </div>
      </section>

{/* Section 5: Jazz Bear Investigation Details */}
      <section 
        className="portfolio-section jazz-bear-details" 
        ref={el => sectionsRef.current[5] = el}
      >
        <div className="section-content">
          <div className="jazz-investigation-content">
            
            {/* Block 1: Text left, fabric image right */}
            <div className="investigation-block block-1" >
              <div className="investigation-text">
                <p className="investigation-main" data-animate data-fade="0.2">
                  Jazz Bear carried double duty during the Mammoth's inaugural season like the ultimate utility player. Now the Mammoth mascot development is "under wraps" – which is basically asking the internet to lose its mind with speculation.
                </p>
                <p className="investigation-reference" data-animate data-fade="0.3">
                <b>The paleontologist deep dive –</b> What did mammoths actually sound like? We're talking to actual scientists about extinct animal vocalizations for a goal horn. The commitment to the bit is everything.
              </p>
               <p className="investigation-reference" data-animate data-fade="0.4">
                <b>Mascot industrial complex exploration – </b>suit designers, costume graveyards, the weird professional ecosystem nobody thinks about.
              </p>
              </div>
              <div className="investigation-image-container" data-animate data-fade="0.5">
                <img src={fabricWorkshop} alt="Fabric workshop with spools" className="investigation-image" />
              </div>
            </div>

            {/* Block 4: Red mascot image left, text right */}
            <div className="investigation-block block-4" data-animate data-fade="0.6">
              <div className="investigation-image-container">
                <img src={redMascotInvestigation} alt="Red mascot investigation" className="investigation-image" />
              </div>
              <div className="investigation-text">
                <p className="investigation-reference" data-animate data-fade="0.7">
                  <b>Short, digestible episodes perfect for platform-hopping – </b>each focused on one absurd rabbit hole. Museum visits, paleontology digs, mascot workshops, fan focus groups debating mammoth proportions
                </p>
                <p className="investigation-reference"  data-animate data-fade="0.8">
                  <b>Behind-the-scenes palace intrigue – </b>Who makes these decisions? How seriously does management take mascot feedback? What happens when the internet has opinions?
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 6: Sequins & Slap Shots - Title */}
       <section 
        className="portfolio-section sequins-title"
        ref={el => sectionsRef.current[6] = el}
      >
        <div className="section-background sequins-bg" >
          <div className="sequins-gradient"></div>
        </div>
        <div className="section-content">
          <div className="sequins-title-content">
            <div className="sequins-character" data-animate data-fade="0.2">
              <img src={skatingCharacter} alt="Skating character" className="character-image" />
            </div>
            <div className="sequins-title-svg">
                <img src={sequinsTitle} alt="Sequins & Slap Shots" className="sequins-svg-title" />
              </div>
              
              <div className="sequins-question-box" data-animate data-fade="0.3">
                <p>What happens when hockey players who've mastered body checks suddenly need to master ballet positions?</p>
              </div>
              <div className="sequins-setup" data-animate data-fade="0.4">
                <h3>The Setup:</h3>
                <p>Send two Mammoth players (or Mammoth hopefuls) through the ultimate performance boot camp – half circus training, half ice theater. They'll learn tightrope walking and trapeze work off-ice, then figure skating choreography and synchronized moves on-ice. These are athletes who've perfected strength and speed learning grace and theatrical flair.</p>
                <p>The guests (judges!?) would be a goldmine -figure skating legends, Cirque Du Soleil veterans, The SLC Real Housewives, teammates and coaches - the "what are you doing" moments write themselves.</p>
                {/*<p>Watch them fail spectacularly at things that require the exact opposite muscle memory they've spent years developing.</p>*/}
              </div>
            
            <div className="sequins-performance-image" data-animate data-fade="0.5">
              <img src={icePerformanceShow} alt="Ice theater performance" className="performance-image" />
            </div>
          </div>
        </div>
      </section>

      {/* Section 7: Content Strategy Details */}
      {/*<section 
        className="portfolio-section content-strategy"
        ref={el => sectionsRef.current[7] = el}
      >
        <div className="section-content">
          <div className="strategy-content">
            <div className="strategy-section" data-animate data-fade="0.5">
              <h2>What We're Capturing</h2>
              <div className="strategy-points">
                <p><strong>The beautiful contradiction</strong> – Players who slam bodies into boards learning delicate pirouettes. Athletes who fight for pucks learning to glide in perfect synchronization. The cognitive dissonance is everything.</p>
                <p><strong>Dual training chaos</strong> – Morning circus camp (juggling, tightrope, trapeze), afternoon ice theater (spins, lifts, dramatic choreography). Each discipline gets its own episode arc, but the crossover moments are pure gold.</p>
                <p><strong>Professional coaches reacting</strong> – Figure skating legends and circus performers watching hockey players attempt their craft. The patience, encouragement, and occasional "what are you doing" moments write themselves.</p>
              </div>
            </div>
            
            <div className="strategy-section" data-animate data-fade="0.7">
              <h2>The Content Goldmine</h2>
              <div className="strategy-points">
                <p><strong>Split-screen comparisons</strong> – Hockey aggression vs. theatrical grace. Checking technique vs. partnered lifts. The visual contrast is instant shareability.</p>
                <p><strong>Unexpected skill transfers</strong> – Maybe hockey balance helps with tightrope walking. Maybe edge work translates to figure skating. Maybe absolutely nothing translates and that's even funnier.</p>
              </div>
            </div>
          </div>
        </div>
      </section>*/}

      {/* Section 8: Company Bio */}
      <section 
        className="portfolio-section company-bio"
        ref={el => sectionsRef.current[7] = el}
      >
        <div className="section-background bio-bg" style={{backgroundImage: `url(${logos})`}}>
          <div className="bio-texture"></div>
        </div>
        <div className="section-content">
          
            <div className="end-grid">
            <div className="brand-projects-horizontal" data-animate data-fade="0.2">
              <span className="brand-subtitle-horizontal" >projects ltd.</span>
            </div>
            <div className="brand-header-vertical" data-animate data-fade="0.3">
              <span className="brand-name-vertical">sterling rain</span>
            </div>
            <div className="bio-content" data-animate data-fade="0.5">
              {/*<p className="bio-philosophy">
                We believe great stories live at the intersection of artistry and authenticity.
              </p>*/}
              <div className="bio-text">
                {/*<p>Sterling Rain Projects was founded on the conviction that branded content deserves the same rigor, craft, and emotional depth as the award-winning documentaries we create for streaming platforms and festivals worldwide. Our approach is rooted in documentary storytelling principles: find the human truth, honor the craft, and never compromise on quality.</p>*/}
                
                <p>Led by a team of award winning filmmakers with deep roots in documentary,  broadcast journalism, and independent cinema, Sterling Rain Projects brings rigor, craft, and emotional depth to every project. From our base in New York, we've built a trusted network of directors, producers, and crews across North America who share our commitment to creatively elevating branded content.</p>
              </div>
              
            
            </div>
          </div>
          <div className="bio-copyright" data-animate data-fade="0.7">
                <p>©2025 Sterling Rain Projects, LTD</p>
              </div>
        </div>
      </section>
    </div>
  );
};

export default PortfolioPage;